import {
  Alert,
  Autocomplete,
  Button,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  ListItemText,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { upsertMapping } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import type { MappingKind, TenantMapping } from '../../api/types';
import { useErrorToast, useSuccessToast } from '../../hooks/useErrorToast';
import { buildTargetOptions, type TargetOption } from './targetOptions';
import { useTargetCatalogue } from './useTargetCatalogue';

/** What the secondary value means differs per code list; only Unit uses it today. */
const secondaryHelpKeys = {
  Unit: 'mappings.dialog.secondaryHelp.Unit',
} as const satisfies Partial<Record<MappingKind, string>>;

const targetHelpKeys = {
  Unit: 'mappings.dialog.targetHelp.Unit',
  PaymentMeans: 'mappings.dialog.targetHelp.PaymentMeans',
  DocumentType: 'mappings.dialog.targetHelp.DocumentType',
  TaxCategory: 'mappings.dialog.targetHelp.TaxCategory',
  TaxExemptionReason: 'mappings.dialog.targetHelp.TaxExemptionReason',
  AllowanceReason: 'mappings.dialog.targetHelp.AllowanceReason',
  ChargeReason: 'mappings.dialog.targetHelp.ChargeReason',
  Country: 'mappings.dialog.targetHelp.Country',
  Currency: 'mappings.dialog.targetHelp.Currency',
  TaxPointDateCode: 'mappings.dialog.targetHelp.TaxPointDateCode',
  PartyIdScheme: 'mappings.dialog.targetHelp.PartyIdScheme',
  StandardItemScheme: 'mappings.dialog.targetHelp.StandardItemScheme',
  ItemClassScheme: 'mappings.dialog.targetHelp.ItemClassScheme',
  AttachmentMimeType: 'mappings.dialog.targetHelp.AttachmentMimeType',
} as const satisfies Record<MappingKind, string>;

// Typing "cash" should find "10 — In cash", not only the codes themselves.
const filterTargetOptions = createFilterOptions<TargetOption>({
  stringify: (option) => `${option.code} ${option.description ?? ''}`,
});

export interface MappingDialogProps {
  type: MappingKind;
  /** Null creates; a mapping edits it in place. */
  mapping: TenantMapping | null;
  onClose: () => void;
}

export function MappingDialog({ type, mapping, onClose }: MappingDialogProps) {
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const toastSuccess = useSuccessToast();
  const { t } = useTranslation();

  const [sourceValue, setSourceValue] = useState(() => mapping?.sourceValue ?? '');
  const [targetValue, setTargetValue] = useState(() => mapping?.targetValue ?? '');
  const [secondaryValue, setSecondaryValue] = useState(() => mapping?.secondaryValue ?? '');
  const [description, setDescription] = useState(() => mapping?.description ?? '');
  const [enabled, setEnabled] = useState(() => mapping?.enabled ?? true);

  // The active code list when one exists, the built-in selection otherwise; either way the same
  // for everybody, so no tenant guard.
  const catalogue = useTargetCatalogue(type);

  const mutation = useMutation({
    mutationFn: () => {
      // The upsert lands in the selected tenant — the same one this list is scoped to, so an
      // edited mapping can only ever be its own row.
      return upsertMapping({
        type,
        sourceValue: sourceValue.trim(),
        targetValue: targetValue.trim(),
        secondaryValue: secondaryValue.trim() === '' ? null : secondaryValue.trim(),
        description: description.trim() === '' ? null : description.trim(),
        enabled,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.mappings.all });
      toastSuccess(t('mappings.saved'));
      onClose();
    },
    onError: toastError,
  });

  const sourceRenamed = mapping !== null && mapping.sourceValue !== sourceValue.trim();
  // A failed query leaves the catalogue empty rather than closing the dialog: the stored value is
  // still offered, so the row stays editable and saveable.
  const targetOptions = buildTargetOptions(catalogue.codes, targetValue);
  // Both the option and the input mirror the one state, so a typed value and a picked value read
  // the same way — the server decides whether an unlisted code is acceptable.
  const selectedTarget: TargetOption | null =
    targetOptions.find((option) => option.code === targetValue) ??
    (targetValue === '' ? null : { code: targetValue, description: null });
  const secondaryHelpKey =
    type in secondaryHelpKeys ? secondaryHelpKeys[type as keyof typeof secondaryHelpKeys] : undefined;

  return (
    <Dialog open onClose={mutation.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mapping ? t('mappings.editMapping') : t('mappings.addMapping')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField
            label={t('mappings.columns.sourceValue')}
            value={sourceValue}
            onChange={(event) => setSourceValue(event.target.value)}
            required
            helperText={t('mappings.dialog.sourceHelp')}
          />

          {sourceRenamed && <Alert severity="warning">{t('mappings.dialog.renameWarning')}</Alert>}

          <Autocomplete
            freeSolo
            options={targetOptions}
            filterOptions={filterTargetOptions}
            loading={catalogue.isPending}
            value={selectedTarget}
            inputValue={targetValue}
            onInputChange={(_event, value) => setTargetValue(value)}
            onChange={(_event, value) =>
              setTargetValue(typeof value === 'string' ? value : (value?.code ?? ''))
            }
            // The closed control shows the code; the open list carries the name.
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.code)}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return (
                <li key={key} {...rest}>
                  <ListItemText
                    primary={
                      option.description === null
                        ? t('mappings.dialog.targetCustom', { value: option.code })
                        : `${option.code} — ${option.description}`
                    }
                  />
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('mappings.columns.targetValue')}
                required
                helperText={
                  catalogue.versionLabel
                    ? `${t(targetHelpKeys[type])} ${t('mappings.dialog.activeVersionHint', { version: catalogue.versionLabel })}`
                    : `${t(targetHelpKeys[type])} ${t('mappings.dialog.targetCustomHint')}`
                }
              />
            )}
          />

          <TextField
            label={t('mappings.dialog.secondaryLabel')}
            value={secondaryValue}
            onChange={(event) => setSecondaryValue(event.target.value)}
            helperText={secondaryHelpKey ? t(secondaryHelpKey) : t('mappings.dialog.secondaryUnused')}
          />

          <TextField
            label={t('columns.description')}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            multiline
            minRows={2}
          />

          <FormControlLabel
            control={<Switch checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />}
            label={t('states.enabled')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          {t('actions.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || sourceValue.trim() === '' || targetValue.trim() === ''}
        >
          {t('actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
